import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, CheckCircle2, XCircle, Loader2, AlertTriangle, Trash2, CreditCard, Lock, Plus, Building2, Ban, Undo2, FileText, RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ISSUERS = ['AMEX', 'AXIS', 'DBS', 'HDFC', 'ICICI', 'IDFC', 'INDUSIND', 'KOTAK', 'RBL', 'SBI', 'YES BANK', 'OTHER'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function BusinessUploadPage() {
  const [tab, setTab] = useState('bank_accounts');
  const [cards, setCards] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [missingData, setMissingData] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardForm, setCardForm] = useState({ card_name: '', issuer: 'ICICI', last_four: '', card_type: 'bank_account', statement_day: 1, region: 'india', pdf_password: '', account_category: 'business' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cardId: null, cardName: '' });
  const fileRef = useRef(null);
  const abortRef = useRef(false);
  const failedFilesRef = useRef([]);

  const isBankTab = tab === 'bank_accounts';

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, missingRes] = await Promise.all([
        axios.get(`${API}/cards?category=business`, { withCredentials: true }),
        axios.get(`${API}/business/missing-statements`, { withCredentials: true }),
      ]);
      setCards(cardsRes.data);
      setMissingData(missingRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addCard = async () => {
    if (!cardForm.card_name || !cardForm.issuer) { toast.error('Name and issuer required'); return; }
    try {
      await axios.post(`${API}/cards`, {
        ...cardForm,
        card_type: isBankTab ? 'bank_account' : 'credit_card',
        account_category: 'business',
      }, { withCredentials: true });
      toast.success(isBankTab ? 'Business bank account added' : 'Business credit card added');
      setShowAddCard(false);
      setCardForm({ card_name: '', issuer: 'ICICI', last_four: '', card_type: 'bank_account', statement_day: 1, region: 'india', pdf_password: '', account_category: 'business' });
      fetchData();
    } catch (err) { toast.error('Failed to add'); }
  };

  const deleteCard = async (id, deleteTransactions) => {
    try {
      const { data } = await axios.delete(`${API}/cards/${id}?delete_transactions=${deleteTransactions}`, { withCredentials: true });
      toast.success(deleteTransactions ? `Deleted with ${data.transactions_deleted} transactions` : 'Deleted — transactions kept');
      setDeleteDialog({ open: false, cardId: null, cardName: '' });
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const tabCards = cards.filter(c =>
    isBankTab ? c.card_type === 'bank_account' : c.card_type === 'credit_card'
  );

  // Upload logic — always passes statement_category=business
  const uploadSingleFile = async (file, queue, idx, total) => {
    const MAX_RETRIES = 3;
    for (let retry = 0; retry < MAX_RETRIES; retry++) {
      if (abortRef.current) return false;
      queue[idx] = { ...queue[idx], status: 'uploading', detail: retry > 0 ? `Retry ${retry}/${MAX_RETRIES - 1}...` : `Uploading ${idx + 1}/${total}...` };
      setUploadQueue([...queue]);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('statement_category', 'business');

      try {
        const { data } = await axios.post(`${API}/statements/upload`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        });
        queue[idx] = { ...queue[idx], status: 'processing', detail: 'AI parsing in progress...', stmtId: data.id };
        setUploadQueue([...queue]);

        let attempts = 0;
        const maxAttempts = 60;
        while (attempts < maxAttempts && !abortRef.current) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const { data: stmt } = await axios.get(`${API}/statements/${data.id}`, { withCredentials: true });
            if (stmt.status === 'completed') {
              const junkInfo = stmt.junk_count ? ` (${stmt.junk_count} junk)` : '';
              const stmtDate = stmt.statement_date || '';
              let monthLabel = '';
              if (stmtDate && stmtDate.length >= 7) {
                const [y, mo] = stmtDate.split('-');
                monthLabel = ` — ${MONTH_SHORT[parseInt(mo) - 1]} ${y}`;
              }
              queue[idx] = { ...queue[idx], status: 'completed', detail: `✓ ${stmt.transaction_count || 0} txns${junkInfo}${monthLabel}` };
              setUploadQueue([...queue]);
              return true;
            } else if (stmt.status === 'error') {
              queue[idx] = { ...queue[idx], status: 'failed', detail: stmt.error || 'Processing failed' };
              setUploadQueue([...queue]);
              return false;
            }
            queue[idx] = { ...queue[idx], detail: `Parsing... (${attempts}s)` };
            setUploadQueue([...queue]);
          } catch { /* poll continue */ }
        }
        queue[idx] = { ...queue[idx], status: 'failed', detail: 'Timeout — check upload history' };
        setUploadQueue([...queue]);
        return false;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Upload failed';
        if (retry === MAX_RETRIES - 1) {
          queue[idx] = { ...queue[idx], status: 'failed', detail: msg };
          setUploadQueue([...queue]);
          return false;
        }
        queue[idx] = { ...queue[idx], detail: `${msg} — retrying...` };
        setUploadQueue([...queue]);
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    return false;
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) { toast.error('Only PDF files are supported'); return; }

    setUploading(true);
    abortRef.current = false;
    failedFilesRef.current = [];
    const queue = pdfFiles.map(f => ({ name: f.name, status: 'pending', detail: '', stmtId: null }));
    setUploadQueue([...queue]);

    let successCount = 0;
    for (let i = 0; i < pdfFiles.length; i++) {
      if (abortRef.current) break;
      if (i > 0) await new Promise(r => setTimeout(r, 1500));
      const ok = await uploadSingleFile(pdfFiles[i], queue, i, pdfFiles.length);
      if (ok) successCount++;
      else failedFilesRef.current.push({ file: pdfFiles[i], idx: i });
    }

    setUploading(false);
    fetchData();
    if (fileRef.current) fileRef.current.value = '';
    if (successCount > 0) toast.success(`Processed ${successCount}/${pdfFiles.length} business statements`);
    if (failedFilesRef.current.length > 0) toast.error(`${failedFilesRef.current.length} files failed — use Retry button`);
  };

  const retryFailed = async () => {
    const failed = failedFilesRef.current;
    if (!failed || failed.length === 0) return;
    setUploading(true);
    abortRef.current = false;
    const queue = [...uploadQueue];
    let successCount = 0;
    const stillFailed = [];
    for (let j = 0; j < failed.length; j++) {
      if (abortRef.current) break;
      if (j > 0) await new Promise(r => setTimeout(r, 1500));
      const { file, idx } = failed[j];
      const ok = await uploadSingleFile(file, queue, idx, queue.length);
      if (ok) successCount++;
      else stillFailed.push({ file, idx });
    }
    failedFilesRef.current = stillFailed;
    setUploading(false);
    fetchData();
    if (successCount > 0) toast.success(`Retried: ${successCount} succeeded`);
    if (stillFailed.length > 0) toast.error(`${stillFailed.length} files still failing`);
  };

  const cancelUpload = () => { abortRef.current = true; };

  const handleSkipMonth = async (accountKey, month) => {
    try {
      await axios.post(`${API}/cards/skip-month`, { account_key: accountKey, month }, { withCredentials: true });
      toast.success('Marked as no transactions');
      fetchData();
    } catch (err) { toast.error('Failed to skip month'); }
  };

  const handleUnskipMonth = async (accountKey, month) => {
    try {
      await axios.delete(`${API}/cards/skip-month`, { data: { account_key: accountKey, month }, withCredentials: true });
      toast.success('Month restored');
      fetchData();
    } catch (err) { toast.error('Failed to restore month'); }
  };

  // Filter sources by current tab
  const bankSources = missingData?.sources?.filter(s => s.type !== 'credit_card') || [];
  const ccSources = missingData?.sources?.filter(s => s.type === 'credit_card') || [];

  // If no business bank sources exist, create a default one showing all months as pending
  const effectiveBankSources = bankSources.length > 0 ? bankSources : (missingData ? [{
    source: 'Business Bank',
    account: '',
    account_key: 'biz|Business Bank',
    label: 'Business Bank Statements',
    type: 'business_bank',
    covered_months: [],
    missing_months: [...(missingData.all_months || [])].reverse(),
    skipped_months: [],
    missing_count: (missingData.all_months || []).length,
    covered_count: 0,
    total_months: (missingData.all_months || []).length,
    active_from: '',
    active_until: '',
  }] : []);

  const activeSources = isBankTab ? effectiveBankSources : ccSources;

  // Coverage grid renderer
  const renderCoverageGrid = (sources, iconEl, typeLabel) => {
    if (sources.length > 0) {
      return (
        <div className="space-y-4">
          {sources.map((src) => {
            const missingMonths = [...(src.missing_months || [])].sort().reverse();
            const skippedMonths = [...(src.skipped_months || [])].sort().reverse();
            const coveredMonths = [...(src.covered_months || [])].sort().reverse();
            const rows = [];
            for (let i = 0; i < missingMonths.length; i += 6) rows.push(missingMonths.slice(i, i + 6));
            const skippedRows = [];
            for (let i = 0; i < skippedMonths.length; i += 6) skippedRows.push(skippedMonths.slice(i, i + 6));
            const coveredRows = [];
            for (let i = 0; i < coveredMonths.length; i += 6) coveredRows.push(coveredMonths.slice(i, i + 6));
            const displayName = src.label || src.source;
            const keyId = `${src.source}-${src.account || ''}`.replace(/\s+/g, '-');
            const accountKey = src.account_key || `${src.source}|${src.account || ''}`;
            return (
              <div key={keyId} className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
                <div className="px-5 py-3 border-b border-[#E2E0D8] bg-[#F7F6F2] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {iconEl}
                    <span className="text-sm font-medium text-[#1C1C19]">{displayName}</span>
                    {(src.missing_count || 0) > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-orange-600">
                        <AlertTriangle size={10} /> {src.missing_count} pending
                      </span>
                    )}
                    {coveredMonths.length > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600">
                        <CheckCircle2 size={10} /> {coveredMonths.length} uploaded
                      </span>
                    )}
                    {skippedMonths.length > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-[#8A8880]">
                        <Ban size={10} /> {skippedMonths.length} skipped
                      </span>
                    )}
                  </div>
                  <Button
                    data-testid={`biz-bulk-upload-${keyId}`}
                    onClick={() => { if (!uploading) fileRef.current?.click(); }}
                    disabled={uploading}
                    className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-7 px-3">
                    <Upload size={12} className="mr-1.5" /> Bulk Upload
                  </Button>
                </div>
                {/* Pending months */}
                {rows.length > 0 && (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E2E0D8]">
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Pending Months</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                          {row.map((m) => {
                            const [y, mo] = m.split('-');
                            const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                            return (
                              <td key={m} className="px-3 py-2.5 align-top">
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-sm font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-200"
                                    data-testid={`biz-month-missing-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                    {label}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => { if (!uploading) fileRef.current?.click(); }}
                                      disabled={uploading}
                                      className="flex items-center gap-1 text-[10px] text-[#284A33] font-medium hover:text-[#1C3624] px-1.5 py-0.5 rounded border border-[#284A33]/20 hover:border-[#284A33]/50 hover:bg-[#284A33]/5 transition-all disabled:opacity-40"
                                      data-testid={`biz-upload-btn-${src.source.replace(/\s+/g, '-')}-${m}`}
                                    >
                                      <Upload size={10} /> Upload
                                    </button>
                                    <button
                                      onClick={() => handleSkipMonth(accountKey, m)}
                                      className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#5C5C55] px-1.5 py-0.5 rounded border border-[#D5D2C6] hover:border-[#8A8880] hover:bg-[#F7F6F2] transition-all"
                                      data-testid={`biz-skip-btn-${src.source.replace(/\s+/g, '-')}-${m}`}
                                    >
                                      <Ban size={10} /> No Txns
                                    </button>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          {Array.from({ length: 6 - row.length }).map((_, i) => (
                            <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {/* Covered months */}
                {coveredRows.length > 0 && (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E2E0D8]">
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-emerald-600 uppercase tracking-wider" colSpan={6}>Uploaded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coveredRows.map((row, ri) => (
                        <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                          {row.map((m) => {
                            const [y, mo] = m.split('-');
                            const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                            return (
                              <td key={m} className="px-3 py-2.5 align-top">
                                <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200"
                                  data-testid={`biz-month-covered-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                  {label} ✓
                                </span>
                              </td>
                            );
                          })}
                          {Array.from({ length: 6 - row.length }).map((_, i) => (
                            <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {/* Skipped months */}
                {skippedRows.length > 0 && (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E2E0D8]">
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#8A8880] uppercase tracking-wider" colSpan={6}>No Transactions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skippedRows.map((row, ri) => (
                        <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                          {row.map((m) => {
                            const [y, mo] = m.split('-');
                            const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                            return (
                              <td key={m} className="px-3 py-2.5 align-top">
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-sm font-medium text-[#8A8880] bg-[#F7F6F2] px-1.5 py-0.5 rounded border border-[#E2E0D8] line-through">{label}</span>
                                  <button
                                    onClick={() => handleUnskipMonth(accountKey, m)}
                                    className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#284A33] px-1.5 py-0.5 rounded border border-[#E2E0D8] hover:border-[#284A33]/30 hover:bg-[#284A33]/5 transition-all">
                                    <Undo2 size={10} /> Undo
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                          {Array.from({ length: 6 - row.length }).map((_, i) => (
                            <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Empty state
    return missingData ? (
      <div className="bg-white border border-[#E2E0D8] rounded-lg p-10 text-center">
        {isBankTab
          ? <Building2 size={40} className="text-[#D5D2C6] mx-auto mb-3" />
          : <CreditCard size={40} className="text-[#D5D2C6] mx-auto mb-3" />}
        <p className="text-sm text-[#5C5C55]">
          No business {isBankTab ? 'bank' : 'credit card'} statements uploaded yet.
        </p>
        <p className="text-xs text-[#8A8880] mt-1">Upload your first statement to start tracking coverage.</p>
        <Button variant="outline" className="mt-3 text-xs border-[#284A33]/30 text-[#284A33]"
          onClick={() => fileRef.current?.click()} data-testid={`biz-upload-first-${isBankTab ? 'bank' : 'cc'}`}>
          <Upload size={14} className="mr-1.5" /> Upload Statement
        </Button>
      </div>
    ) : (
      <div className="p-8 text-center text-[#5C5C55] text-sm">Loading coverage data...</div>
    );
  };

  // ======= INVOICE UPLOAD PANEL (inline) =======
  const InvoiceUploadContent = () => {
    const [invoices, setInvoices] = useState([]);
    const [loadingInv, setLoadingInv] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [bulkDialog, setBulkDialog] = useState(false);
    const [bulkVendor, setBulkVendor] = useState('');
    const [bulkMonth, setBulkMonth] = useState('');
    const [bulkFiles, setBulkFiles] = useState([]);
    const [uploadingInv, setUploadingInv] = useState(null);
    const bulkFileRef = useRef(null);
    const invoiceFileRef = useRef(null);

    const KNOWN_PROVIDERS = ['Google Workspace', 'Airtel', 'IFANOW (My Planner)', 'Emirates', 'Air India', 'WeWork'];

    const loadInvoices = useCallback(async () => {
      setLoadingInv(true);
      try {
        const { data } = await axios.get(`${API}/business/invoices`, { withCredentials: true });
        setInvoices(data.invoices || []);
      } catch { setInvoices([]); }
      setLoadingInv(false);
    }, []);

    useEffect(() => { loadInvoices(); }, [loadInvoices]);

    const fetchFromEmail = async () => {
      setFetching(true);
      try {
        await axios.post(`${API}/business/invoices/fetch`, {}, { withCredentials: true });
        toast.info('Fetching invoices from email... This may take a minute.');
        const poll = setInterval(async () => {
          try {
            const { data } = await axios.get(`${API}/business/invoices/fetch-status`, { withCredentials: true });
            if (data.status === 'complete') {
              clearInterval(poll);
              setFetching(false);
              toast.success(`Fetched ${data.total_fetched} invoice(s)${data.total_skipped > 0 ? `, ${data.total_skipped} already existed` : ''}`);
              loadInvoices();
            }
          } catch {}
        }, 3000);
        setTimeout(() => { clearInterval(poll); setFetching(false); }, 180000);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to start fetch');
        setFetching(false);
      }
    };

    const uploadInvoice = async (file, provider, monthKey) => {
      setUploadingInv({ provider, month: monthKey });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('provider', provider);
      formData.append('month', monthKey);
      try {
        const { data } = await axios.post(`${API}/business/invoices/upload`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.status === 'skipped') toast.info('Invoice already exists');
        else { toast.success(`Uploaded ${data.filename}`); loadInvoices(); }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed');
      }
      setUploadingInv(null);
    };

    const triggerSingleUpload = (provider, monthKey) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf';
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) uploadInvoice(file, provider, monthKey);
      };
      input.click();
    };

    const handleBulkUpload = async () => {
      if (!bulkVendor || !bulkMonth || bulkFiles.length === 0) {
        toast.error('Select vendor, month and files');
        return;
      }
      for (const file of bulkFiles) {
        await uploadInvoice(file, bulkVendor, bulkMonth);
      }
      setBulkDialog(false);
      setBulkFiles([]);
      setBulkVendor('');
      setBulkMonth('');
    };

    // Group invoices by provider
    const byProvider = {};
    invoices.forEach(inv => {
      const p = inv.provider || 'Other';
      if (!byProvider[p]) byProvider[p] = {};
      const mk = inv.month || 'unknown';
      if (!byProvider[p][mk]) byProvider[p][mk] = [];
      byProvider[p][mk].push(inv);
    });

    const allProviders = [...new Set([...KNOWN_PROVIDERS, ...Object.keys(byProvider)])].sort();

    // Build month keys from Apr 2025 to current
    const buildMonthKeys = () => {
      const now = new Date();
      const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const keys = [];
      let y = 2025, m = 4;
      while (true) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        if (key > currentKey) break;
        keys.push(key);
        m++;
        if (m > 12) { m = 1; y++; }
      }
      return keys.reverse();
    };
    const allMonthKeys = buildMonthKeys();

    if (loadingInv) {
      return <div className="p-8 text-center text-sm text-[#5C5C55]">Loading invoices...</div>;
    }

    return (
      <div className="space-y-4" data-testid="biz-invoice-upload-panel">
        {/* Fetch from email bar — same style as CC fetch bar */}
        <div className="flex items-center gap-3 flex-wrap bg-[#F7F6F2] border border-[#E2E0D8] rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <RefreshCw size={14} className="text-blue-600 shrink-0" />
            <span className="text-xs text-[#5C5C55]">Fetch from email:</span>
            <Button onClick={fetchFromEmail} disabled={fetching} variant="outline" size="sm"
              data-testid="biz-inv-fetch-email-btn"
              className="border-blue-200 text-blue-700 hover:bg-blue-50 text-xs h-7 px-2">
              {fetching ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
              {fetching ? 'Fetching...' : 'All Invoices'}
            </Button>
          </div>
          <div className="ml-auto">
            <Button onClick={() => setBulkDialog(true)} variant="outline" data-testid="biz-inv-bulk-upload-btn"
              className="border-[#E2E0D8] text-[#1C1C19] hover:bg-[#F7F6F2] h-7 px-3 text-xs">
              <Upload size={12} className="mr-1.5" /> Bulk Upload
            </Button>
          </div>
        </div>

        {/* Vendor-wise coverage — exact same layout as bank/CC sections */}
        {allProviders.length > 0 ? (
          <div className="space-y-4">
            {allProviders.map((provider) => {
              const monthMap = byProvider[provider] || {};
              const missingMonths = [...allMonthKeys.filter(k => !monthMap[k]?.length)];
              const coveredMonths = [...allMonthKeys.filter(k => monthMap[k]?.length > 0)];
              const keyId = provider.replace(/[\s()/]+/g, '-');

              const rows = [];
              for (let i = 0; i < missingMonths.length; i += 6) rows.push(missingMonths.slice(i, i + 6));
              const coveredRows = [];
              for (let i = 0; i < coveredMonths.length; i += 6) coveredRows.push(coveredMonths.slice(i, i + 6));

              if (rows.length === 0 && coveredRows.length === 0) return null;

              return (
                <div key={provider} className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
                  {/* Vendor header — matches bank/CC header style exactly */}
                  <div className="px-5 py-3 border-b border-[#E2E0D8] bg-[#F7F6F2] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText size={14} className="text-[#284A33] shrink-0" />
                      <span className="text-sm font-medium text-[#1C1C19]">{provider}</span>
                      {missingMonths.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-orange-600">
                          <AlertTriangle size={10} /> {missingMonths.length} pending
                        </span>
                      )}
                      {coveredMonths.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600">
                          <CheckCircle2 size={10} /> {coveredMonths.length} uploaded
                        </span>
                      )}
                    </div>
                    <Button
                      data-testid={`biz-inv-vendor-upload-${keyId}`}
                      onClick={() => { setBulkVendor(provider); setBulkDialog(true); }}
                      className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-7 px-3">
                      <Upload size={12} className="mr-1.5" /> Bulk Upload
                    </Button>
                  </div>

                  {/* Pending months — red pills with Upload button, same as CC/Bank */}
                  {rows.length > 0 && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#E2E0D8]">
                          <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Pending Months</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                            {row.map((mk) => {
                              const [yr, mo] = mk.split('-');
                              const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${yr.slice(2)}`;
                              const isUploading = uploadingInv?.provider === provider && uploadingInv?.month === mk;
                              return (
                                <td key={mk} className="px-3 py-2.5 align-top">
                                  <div className="flex flex-col items-start gap-1">
                                    <span className="text-sm font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-200"
                                      data-testid={`biz-inv-missing-${keyId}-${mk}`}>
                                      {label}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => triggerSingleUpload(provider, mk)}
                                        disabled={isUploading}
                                        className="flex items-center gap-1 text-[10px] text-[#284A33] font-medium hover:text-[#1C3624] px-1.5 py-0.5 rounded border border-[#284A33]/20 hover:border-[#284A33]/50 hover:bg-[#284A33]/5 transition-all disabled:opacity-40"
                                        data-testid={`biz-inv-upload-single-${keyId}-${mk}`}
                                      >
                                        {isUploading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />} Upload
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                            {Array.from({ length: 6 - row.length }).map((_, i) => (
                              <td key={`e-${i}`} className="px-3 py-2.5"></td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Uploaded months — green pills, same as bank uploaded section */}
                  {coveredRows.length > 0 && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#E2E0D8]">
                          <th className="text-left px-5 py-2 text-[10px] font-semibold text-emerald-600 uppercase tracking-wider" colSpan={6}>Uploaded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coveredRows.map((row, ri) => (
                          <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                            {row.map((mk) => {
                              const [yr, mo] = mk.split('-');
                              const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${yr.slice(2)}`;
                              return (
                                <td key={mk} className="px-3 py-2.5 align-top">
                                  <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200"
                                    data-testid={`biz-inv-avail-${keyId}-${mk}`}>
                                    {label} ✓
                                  </span>
                                </td>
                              );
                            })}
                            {Array.from({ length: 6 - row.length }).map((_, i) => (
                              <td key={`e-${i}`} className="px-3 py-2.5"></td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-[#E2E0D8] rounded-lg p-10 text-center">
            <FileText size={40} className="text-[#D5D2C6] mx-auto mb-3" />
            <p className="text-sm text-[#5C5C55]">No invoice vendors found. Fetch from email to get started.</p>
            <Button variant="outline" className="mt-3 text-xs border-[#284A33]/30 text-[#284A33]"
              onClick={fetchFromEmail} data-testid="biz-inv-fetch-first">
              <RefreshCw size={14} className="mr-1.5" /> Fetch from Email
            </Button>
          </div>
        )}

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
          <DialogContent className="max-w-md" data-testid="biz-inv-bulk-dialog">
            <DialogHeader>
              <DialogTitle>Bulk Upload Invoices</DialogTitle>
              <DialogDescription>Upload one or more invoice PDFs for a specific vendor and month.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs text-[#5C5C55]">Vendor</Label>
                <Select value={bulkVendor} onValueChange={setBulkVendor}>
                  <SelectTrigger className="mt-1" data-testid="biz-inv-bulk-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {allProviders.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#5C5C55]">Month</Label>
                <Select value={bulkMonth} onValueChange={setBulkMonth}>
                  <SelectTrigger className="mt-1" data-testid="biz-inv-bulk-month"><SelectValue placeholder="Select month" /></SelectTrigger>
                  <SelectContent>
                    {allMonthKeys.map(mk => {
                      const [yr, mo] = mk.split('-');
                      const label = `${MONTH_SHORT[parseInt(mo) - 1]} ${yr}`;
                      return <SelectItem key={mk} value={mk}>{label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#5C5C55]">PDF Files</Label>
                <div className="mt-1">
                  <input ref={bulkFileRef} type="file" accept=".pdf" multiple
                    onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
                    className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#284A33]/10 file:text-[#284A33] hover:file:bg-[#284A33]/20"
                    data-testid="biz-inv-bulk-files" />
                  {bulkFiles.length > 0 && (
                    <p className="text-[10px] text-[#5C5C55] mt-1">{bulkFiles.length} file(s) selected</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialog(false)}>Cancel</Button>
              <Button data-testid="biz-inv-bulk-submit" onClick={handleBulkUpload}
                disabled={!bulkVendor || !bulkMonth || bulkFiles.length === 0}
                className="bg-[#284A33] text-white hover:bg-[#1C3624]">
                <Upload size={14} className="mr-1.5" /> Upload {bulkFiles.length > 0 ? `${bulkFiles.length} File(s)` : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1200px]" data-testid="business-upload-page">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Business Upload</h2>
        <p className="text-sm text-[#5C5C55] mt-1">Upload business bank and credit card statements. Track month-wise coverage.</p>
      </div>

      {/* Hidden file input */}
      <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
        onChange={(e) => handleUpload(e.target.files)} data-testid="business-upload-file-input" />

      {/* Tabs + Add button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-[#E8E6DE] p-1 rounded-lg">
          <button data-testid="biz-tab-bank-accounts"
            onClick={() => setTab('bank_accounts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'bank_accounts' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
            <Building2 size={15} /> Bank Accounts
          </button>
          <button data-testid="biz-tab-credit-cards"
            onClick={() => setTab('credit_cards')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'credit_cards' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
            <CreditCard size={15} /> Credit Cards
          </button>
          <button data-testid="biz-tab-invoices"
            onClick={() => setTab('invoices')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'invoices' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
            <FileText size={15} /> Invoices
          </button>
        </div>
      {tab !== 'invoices' && (
        <Button data-testid="biz-add-card-btn"
          onClick={() => {
            setCardForm({ card_name: '', issuer: 'ICICI', last_four: '', card_type: isBankTab ? 'bank_account' : 'credit_card', statement_day: 1, region: 'india', pdf_password: '', account_category: 'business' });
            setShowAddCard(true);
          }}
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8">
          <Plus size={14} className="mr-1.5" /> Add {isBankTab ? 'Bank Account' : 'Credit Card'}
        </Button>
      )}
      </div>

      {/* Registered accounts/cards (hidden for invoices tab) */}
      {tab !== 'invoices' && tabCards.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-[#5C5C55] uppercase tracking-wider font-medium">
            Registered {isBankTab ? 'Business Accounts' : 'Business Cards'}
          </p>
          <div className="flex flex-wrap gap-2">
            {tabCards.map(card => (
              <div key={card.id} className="flex items-center gap-2 bg-white border border-[#E2E0D8] rounded-md px-3 py-2 text-xs">
                {isBankTab
                  ? <Building2 size={12} className="text-[#284A33]" />
                  : <CreditCard size={12} className="text-[#284A33]" />}
                <span className="font-medium text-[#1C1C19]">{card.card_name}</span>
                <span className="text-[9px] uppercase bg-[#E8E6DE] text-[#5C5C55] px-1 py-0.5 rounded">{card.issuer}</span>
                {card.last_four && <span className="font-mono text-[#5C5C55]">{isBankTab ? 'A/c' : '••••'} {card.last_four}</span>}
                {card.pdf_password && <Lock size={9} className="text-[#5C5C55]" />}
                <button onClick={() => setDeleteDialog({ open: true, cardId: card.id, cardName: card.card_name })}
                  className="text-[#B5B3A9] hover:text-red-500 transition-colors ml-1"><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coverage Grid / Invoice Upload */}
      {tab === 'invoices' ? (
        <InvoiceUploadContent />
      ) : isBankTab ? (
        renderCoverageGrid(
          effectiveBankSources,
          <Building2 size={14} className="text-[#284A33] shrink-0" />,
          'bank'
        )
      ) : (
        renderCoverageGrid(
          ccSources,
          <CreditCard size={14} className="text-[#284A33] shrink-0" />,
          'credit card'
        )
      )}

      {/* Upload Progress */}
      {uploadQueue.length > 0 && (() => {
        const completed = uploadQueue.filter(q => q.status === 'completed').length;
        const failed = uploadQueue.filter(q => q.status === 'failed').length;
        const total = uploadQueue.length;
        const pct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
        const activeItem = uploadQueue.find(q => q.status === 'uploading' || q.status === 'processing');
        return (
          <div className={`${uploading ? 'sticky top-0 z-30' : ''} space-y-2 bg-white border border-[#E2E0D8] rounded-lg p-4 shadow-sm`} data-testid="business-upload-queue">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {uploading ? <Loader2 size={16} className="text-[#284A33] animate-spin" /> : <CheckCircle2 size={16} className="text-emerald-600" />}
                <span className="text-sm font-medium text-[#1C1C19]">
                  {uploading ? `Processing ${completed + 1} of ${total}...` : `Upload complete — ${completed} succeeded${failed > 0 ? `, ${failed} failed` : ''}`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-[#5C5C55]">{completed}/{total}</span>
                {uploading && (
                  <button onClick={cancelUpload} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid="biz-cancel-upload">Cancel</button>
                )}
                {!uploading && failedFilesRef.current.length > 0 && (
                  <button onClick={retryFailed} data-testid="biz-retry-failed-btn" className="text-xs font-medium text-[#D36135] hover:text-[#B5502A] underline">
                    Retry {failedFilesRef.current.length} failed
                  </button>
                )}
                {!uploading && (
                  <button onClick={() => setUploadQueue([])} className="text-xs text-[#5C5C55] hover:text-[#1C1C19]">Dismiss</button>
                )}
              </div>
            </div>
            <div className="w-full bg-[#E8E6DE] rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all duration-500 ${failed > 0 && !uploading ? 'bg-orange-400' : 'bg-[#284A33]'}`}
                style={{ width: `${pct}%` }} />
            </div>
            {activeItem && (
              <div className="flex items-center gap-2 text-xs text-[#5C5C55] bg-[#F7F6F2] rounded px-3 py-1.5">
                <Loader2 size={12} className="animate-spin text-[#284A33]" />
                <span className="font-medium text-[#1C1C19] truncate">{activeItem.name}</span>
                <span className="ml-auto shrink-0">{activeItem.detail}</span>
              </div>
            )}
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {uploadQueue.map((item, i) => (
                <div key={i} className={`flex items-center gap-2 rounded px-3 py-1.5 text-xs ${
                  item.status === 'failed' ? 'bg-red-50' :
                  item.status === 'completed' ? 'bg-emerald-50' :
                  item.status === 'processing' || item.status === 'uploading' ? 'bg-[#284A33]/5' : 'bg-[#F7F6F2]'
                }`}>
                  {item.status === 'completed' && <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />}
                  {item.status === 'failed' && <XCircle size={12} className="text-red-500 shrink-0" />}
                  {item.status === 'pending' && <div className="w-3 h-3 rounded-full border-2 border-[#D5D2C6] shrink-0" />}
                  {(item.status === 'processing' || item.status === 'uploading') && <Loader2 size={12} className="text-[#284A33] animate-spin shrink-0" />}
                  <span className={`truncate ${item.status === 'failed' ? 'text-red-700' : 'text-[#1C1C19]'} font-medium`}>{item.name}</span>
                  {item.detail && <span className={`ml-auto shrink-0 ${item.status === 'completed' ? 'text-emerald-700' : 'text-[#5C5C55]'}`}>{item.detail}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="max-w-md" data-testid="biz-add-card-dialog">
          <DialogHeader>
            <DialogTitle>Add Business {isBankTab ? 'Bank Account' : 'Credit Card'}</DialogTitle>
            <DialogDescription>Register a new business {isBankTab ? 'bank account' : 'credit card'} for statement tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-[#5C5C55]">Name</Label>
              <Input data-testid="biz-card-name" value={cardForm.card_name} onChange={e => setCardForm(f => ({...f, card_name: e.target.value}))}
                placeholder={isBankTab ? 'e.g. ICICI Current A/c' : 'e.g. ICICI Emirates'} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#5C5C55]">Issuer / Bank</Label>
              <Select value={cardForm.issuer} onValueChange={v => setCardForm(f => ({...f, issuer: v}))}>
                <SelectTrigger className="mt-1" data-testid="biz-card-issuer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ISSUERS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#5C5C55]">Last 4 digits</Label>
              <Input data-testid="biz-card-last-four" value={cardForm.last_four} onChange={e => setCardForm(f => ({...f, last_four: e.target.value.replace(/\D/g, '').slice(0, 4)}))}
                placeholder="1234" maxLength={4} className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs text-[#5C5C55]">PDF Password (optional)</Label>
              <Input data-testid="biz-card-password" type="password" value={cardForm.pdf_password} onChange={e => setCardForm(f => ({...f, pdf_password: e.target.value}))}
                placeholder="If statements are password-protected" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCard(false)}>Cancel</Button>
            <Button data-testid="biz-save-card-btn" onClick={addCard} className="bg-[#284A33] text-white hover:bg-[#1C3624]">
              Save {isBankTab ? 'Account' : 'Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(v) => { if (!v) setDeleteDialog({ open: false, cardId: null, cardName: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="biz-delete-card-dialog">
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.cardName}?</DialogTitle>
            <DialogDescription>Do you also want to delete all its transactions?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => deleteCard(deleteDialog.cardId, false)}>Keep Transactions</Button>
            <Button variant="destructive" onClick={() => deleteCard(deleteDialog.cardId, true)} data-testid="biz-delete-with-txns">Delete All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
